import React from 'react';
import { Separator, Stack } from '@fluentui/react';
import { DialogCardError } from 'common/components/Cards/DialogCard/DialogCardError';
import { Panel } from 'common/components/Panel';
import { usePanelMobileView } from 'common/components/Panel/usePanelMobileView';
import { ModalShimmer } from 'common/components/Shimmer/DefaultShimmers/ModalShimmer';
import { pageDataService } from 'common/utilities';
import { useLogViewOnMount } from 'common/utilities/telemetryHelpers';
import { useLocalizedStrings } from 'i18n';
import { FamilyMember } from 'mcsclient/models';
import { isApiCallComplete, isApiCallFailed } from 'store/apiCallResultStatus';
import { useNotificationSettingsSummary } from 'store/notifications/useNotificationSettings';
import { useRaiseWebViewRenderedEvent } from 'store/webView/useWebView';

import { IOfficeShellPanelProps } from '../OfficeShellPanel.types';

import { NotificationSettingsConfirmation } from './Confirmation/NotificationSettingsConfirmation';
import {
  NotificationSettingsConfirmationContext,
  useNotificationSettingsConfirmation,
} from './Confirmation/NotificationSettingsConfirmationContext';
import { EmailNotificationSettings } from './Email/EmailNotificationSettings';
import { EmailNotificationSettingsSummary } from './Email/EmailNotificationSettingsSummary';
import { FamilyMemberNotificationSettings } from './Family/FamilyMemberNotificationSettings';
import { FamilyNotificationSettingsSummary } from './Family/FamilyNotificationSettingsSummary';
import { NativeNotificationSettings } from './Native/NativeNotificationSettings';
import { PushNotificationSettings } from './Push/PushNotificationSettings';
import { PushNotificationSettingsSummary } from './Push/PushNotificationSettingsSummary';
import { stackTokens, useNotificationSettingsStyles } from './NotificationSettings.styles';
import { NotificationSettingType } from './NotificationSettings.types';

export const NotificationSettingsPanel = (props: IOfficeShellPanelProps<'NotificationSettings'>) => {
  useRaiseWebViewRenderedEvent('NotificationSettings', false, false);
  useLogViewOnMount('NotificationSettings');
  const { closeButtonAriaLabel, onDismiss, deepLinkParams, ...otherProps } = props;
  const { notificationSettingsSummaryLoadStatus, notificationSettingsSummary, forceReloadSettings } = useNotificationSettingsSummary();
  const classNames = useNotificationSettingsStyles();
  const { isMobileView } = usePanelMobileView();

  const { confirmationProps, showConfirmationPanel, exitConfirmationPanel } = useNotificationSettingsConfirmation();
  const confirmationContextValue = React.useMemo(() => ({ showConfirmationPanel }), [showConfirmationPanel]);

  const { t } = useLocalizedStrings();

  const [selectedSettingType, setSelectedSettingType] = React.useState<NotificationSettingType | null>(null);
  const [selectedFamilyMember, setSelectedFamilyMember] = React.useState<FamilyMember | null>(null);

  const isLoading = !isApiCallComplete(notificationSettingsSummaryLoadStatus);
  const hasError = isApiCallFailed(notificationSettingsSummaryLoadStatus);

  // Handle deep link params
  const [isDeepLinkHandled, setIsDeepLinkHandled] = React.useState(false);
  React.useEffect(() => {
    if (isDeepLinkHandled || isLoading) {
      return;
    }

    const { settingType } = deepLinkParams ?? {};

    // family deep link not supported yet
    if (settingType && settingType !== 'Family') {
      setSelectedSettingType(settingType);
    }

    setIsDeepLinkHandled(true);
  }, [deepLinkParams, isDeepLinkHandled, isLoading]);

  React.useEffect(() => {
    // Make sure to always reload settings on mount
    forceReloadSettings();
  }, [forceReloadSettings]);

  const drillDownDelegate = React.useCallback(
    (settingType: NotificationSettingType) => (familyMember?: FamilyMember) => {
      setSelectedSettingType(settingType);
      setSelectedFamilyMember(familyMember || null);
    },
    []
  );
  const exitDrillDownView = React.useCallback(() => {
    setSelectedSettingType(null);
    setSelectedFamilyMember(null);
  }, []);

  const renderSeparator = React.useCallback(
    () => !isMobileView && <Separator className={classNames.separator} />,
    [classNames.separator, isMobileView]
  );

  const getHeaderText = React.useCallback(() => {
    if (!selectedSettingType) {
      return t('OfficeShellPanel.NotificationSettings.Header');
    }
    return {
      Push: t('OfficeShellPanel.NotificationSettings.Sections.Push.Title'),
      Email: t('OfficeShellPanel.NotificationSettings.Sections.Email.Title'),
      Family: t('OfficeShellPanel.NotificationSettings.Sections.Family.Title'),
    }[selectedSettingType];
  }, [selectedSettingType, t]);

  const renderContents = React.useCallback(() => {
    if (isLoading) {
      return <ModalShimmer />;
    }
    if (hasError) {
      return (
        <DialogCardError
          title={t('OfficeShellPanel.NotificationSettings.Error.Title')}
          subtitle={t('OfficeShellPanel.NotificationSettings.Error.Subtitle')}
        />
      );
    }

    if (confirmationProps) {
      return <NotificationSettingsConfirmation {...confirmationProps} />;
    }
    if (selectedSettingType) {
      return (
        <>
          {selectedSettingType === 'Push' && <PushNotificationSettings />}
          {selectedSettingType === 'Email' && <EmailNotificationSettings />}
          {selectedSettingType === 'Family' && selectedFamilyMember && (
            <FamilyMemberNotificationSettings member={selectedFamilyMember} />
          )}
        </>
      );
    }
    return (
      <Stack tokens={isMobileView ? stackTokens.gapXXL : stackTokens.gapXL}>
        {pageDataService.isWebView() && (
          <>
            <NativeNotificationSettings />
            {renderSeparator()}
          </>
        )}
        <PushNotificationSettingsSummary
          onDrillDown={drillDownDelegate('Push')}
          drillDownButtonAriaLabel={t('OfficeShellPanel.NotificationSettings.Sections.Push.DrillDownButtonAriaLabel')}
        />
        {renderSeparator()}
        <EmailNotificationSettingsSummary
          onDrillDown={drillDownDelegate('Email')}
          drillDownButtonAriaLabel={t('OfficeShellPanel.NotificationSettings.Sections.Email.DrillDownButtonAriaLabel')}
        />
        {renderSeparator()}
        {notificationSettingsSummary?.familyNotificationSetting && (
          <FamilyNotificationSettingsSummary onDrillDown={drillDownDelegate('Family')} />
        )}
      </Stack>
    );
  }, [
    renderSeparator,
    drillDownDelegate,
    hasError,
    isLoading,
    isMobileView,
    notificationSettingsSummary?.familyNotificationSetting,
    selectedFamilyMember,
    selectedSettingType,
    confirmationProps,
    t,
  ]);

  const getBackClickAction = React.useCallback(() => {
    if (confirmationProps) {
      return exitConfirmationPanel;
    }
    if (selectedSettingType) {
      return exitDrillDownView;
    }
    return undefined;
  }, [confirmationProps, exitConfirmationPanel, selectedSettingType, exitDrillDownView]);

  const getBackAriaLabel = React.useCallback(() => {
    // User is on main notification settings panel (return undefined to fall back to default aria-label in Panel component)
    if (!selectedSettingType && !confirmationProps) {
      return undefined;
    }

    // User is in confirmation state - clicking "Back" will take them to the previous setting panel (i.e. panel for the selected setting)
    if (confirmationProps && selectedSettingType) {
      const memberName =
        selectedFamilyMember?.firstName || selectedFamilyMember?.displayName || selectedFamilyMember?.userPrincipalName;
      switch (selectedSettingType) {
        case 'Push':
          return t('OfficeShellPanel.NotificationSettings.BackToPushNotificationSettings');
        case 'Email':
          return t('OfficeShellPanel.NotificationSettings.BackToEmailNotificationSettings');
        case 'Family':
          if (memberName) {
            return t('OfficeShellPanel.NotificationSettings.BackToSpecificFamilyMemberNotificationSettings', { memberName });
          }
          return t('OfficeShellPanel.NotificationSettings.BackToFamilyMemberNotificationSettings');

        default:
          break;
      }
    }

    // User will return to the main notification settings panel from the confirmation panel or from the selected setting panel
    return t('OfficeShellPanel.NotificationSettings.BackToMainNotificationSettings');
  }, [confirmationProps, selectedSettingType, selectedFamilyMember, t]);

  return (
    <NotificationSettingsConfirmationContext.Provider value={confirmationContextValue}>
      <Panel
        isBlocking
        isLightDismiss
        onDismiss={onDismiss}
        closeButtonAriaLabel={closeButtonAriaLabel}
        headerText={!confirmationProps ? getHeaderText() : undefined}
        enableBackNavigation={!!selectedSettingType || !!confirmationProps}
        onBackLinkClick={getBackClickAction()}
        backAriaLabel={getBackAriaLabel()}
        {...otherProps}
      >
        {renderContents()}
      </Panel>
    </NotificationSettingsConfirmationContext.Provider>
  );
};
